import { createApi } from '@reduxjs/toolkit/query/react';
import { DateTime } from 'luxon';
import {
  Manufacturer,
  ManufacturerFilters,
  PaginatedResponse,
  PaginationParams,
  SortParams,
} from '../../types';
import { applyDirection, baseQueryStub, compareValues, logError } from '../../utils';
import { ManufacturerService } from './manufacturer-service';

// Improve pagination logic to work with cached data
const paginateManufacturers = (
  manufacturers: Manufacturer[],
  pagination: PaginationParams,
  filters?: ManufacturerFilters,
  sort?: SortParams
): PaginatedResponse<Manufacturer> => {
  // Apply filters if provided
  let filteredData = [...manufacturers];

  if (filters) {
    filteredData = filteredData.filter(manufacturer => {
      return Object.entries(filters).every(([key, value]) => {
        // Skip undefined, null, or empty string filters
        if (value === undefined || value === null || value === '') return true;

        const manufacturerValue = manufacturer[key as keyof Manufacturer];

        // Handle status specially since it's a boolean but might come as a string
        if (key === 'status' && typeof value === 'string') {
          return manufacturerValue === (value === 'true');
        }

        // Case-insensitive string search for string values
        if (typeof manufacturerValue === 'string' && typeof value === 'string') {
          return manufacturerValue.toLowerCase().includes(value.toLowerCase());
        }

        return manufacturerValue === value;
      });
    });
  }

  // Apply sorting if provided
  if (sort?.field && sort?.direction) {
    filteredData.sort((a, b) => {
      const fieldKey = sort.field as keyof Manufacturer;
      const valueA = a[fieldKey];
      const valueB = b[fieldKey];

      const result = compareValues(valueA, valueB);
      return applyDirection(result, sort.direction);
    });
  }

  // Apply pagination
  const { page = 1, pageSize = 10 } = pagination;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    totalCount: filteredData.length,
    page,
    pageSize,
    totalPages: Math.ceil(filteredData.length / pageSize),
  };
};

// A helper function to initialize the service once when needed
let manufacturerService: ManufacturerService | null = null;
const getManufacturerService = () => {
  manufacturerService ??= new ManufacturerService();
  return manufacturerService;
};

// Cache for all manufacturers
let allManufacturersCache: {
  data: Manufacturer[] | null;
  timestamp: number;
  expiryTime: number;
} = {
  data: null,
  timestamp: 0,
  expiryTime: 5 * 60 * 1000, // 5 minutes in milliseconds
};

// Function to get all manufacturers with caching
const getAllManufacturersWithCache = async (): Promise<Manufacturer[]> => {
  const currentTime = DateTime.now().toMillis();

  // If cache is valid, return cached data
  if (
    allManufacturersCache.data !== null &&
    currentTime - allManufacturersCache.timestamp < allManufacturersCache.expiryTime
  ) {
    return allManufacturersCache.data;
  }

  // Cache is invalid or expired, fetch fresh data
  try {
    const manufacturerService = getManufacturerService();
    const manufacturers = await manufacturerService.getAllManufacturers();

    // Update cache
    allManufacturersCache = {
      data: manufacturers,
      timestamp: currentTime,
      expiryTime: allManufacturersCache.expiryTime,
    };

    return manufacturers;
  } catch (error) {
    // If there's an error but we have cached data, return it even if expired
    if (allManufacturersCache.data !== null) {
      logError('Error fetching fresh manufacturers data, using expired cache:', error);
      return allManufacturersCache.data;
    }

    // No cached data available, must throw error
    throw error;
  }
};

// Function to get active manufacturers from cache, sorted alphabetically by name
const getActiveManufacturersFromCache = async (): Promise<Manufacturer[]> => {
  const allManufacturers = await getAllManufacturersWithCache();

  return allManufacturers
    .filter(manufacturer => manufacturer.status)
    .sort((a, b) => a.licenseeName.toLowerCase().localeCompare(b.licenseeName.toLowerCase()));
};

// Function to invalidate the cache
const invalidateManufacturersCache = () => {
  allManufacturersCache.data = null;
  allManufacturersCache.timestamp = 0;
};

// Define an API service using RTK Query that uses the ManufacturerService
export const manufacturersApi = createApi({
  reducerPath: 'manufacturersApi',
  baseQuery: baseQueryStub,
  tagTypes: ['Manufacturer'],
  endpoints: builder => ({
    getManufacturers: builder.query<
      PaginatedResponse<Manufacturer>,
      {
        pagination: PaginationParams;
        filters?: ManufacturerFilters;
        sort?: SortParams;
      }
    >({
      queryFn: async ({ pagination, filters, sort }) => {
        // Use the cached version of getAllManufacturers
        const allManufacturers = await getAllManufacturersWithCache();

        // Apply client-side pagination, filtering, and sorting
        const paginatedResponse = paginateManufacturers(
          allManufacturers,
          pagination,
          filters,
          sort
        );

        return { data: paginatedResponse };
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Manufacturer' as const, id })),
              { type: 'Manufacturer', id: 'LIST' },
            ]
          : [{ type: 'Manufacturer', id: 'LIST' }],
    }),
    getAllManufacturers: builder.query<Manufacturer[], void>({
      queryFn: async () => {
        // Use the cached version
        const manufacturers = await getAllManufacturersWithCache();
        return { data: manufacturers };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Manufacturer' as const, id })),
              { type: 'Manufacturer', id: 'LIST' },
            ]
          : [{ type: 'Manufacturer', id: 'LIST' }],
    }),
    getActiveManufacturers: builder.query<Manufacturer[], void>({
      queryFn: async () => {
        // Get active manufacturers from cache, sorted alphabetically
        const activeManufacturers = await getActiveManufacturersFromCache();
        return { data: activeManufacturers };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Manufacturer' as const, id })),
              { type: 'Manufacturer', id: 'LIST' },
            ]
          : [{ type: 'Manufacturer', id: 'LIST' }],
    }),
    getManufacturerById: builder.query<Manufacturer, string>({
      queryFn: async id => {
        // Don't go against cache. Get latest version.
        const manufacturerService = getManufacturerService();
        const manufacturer = await manufacturerService.getManufacturerById(id);
        return { data: manufacturer };
      },
      providesTags: (_, __, id) => [{ type: 'Manufacturer', id }],
    }),
    addManufacturer: builder.mutation<
      Manufacturer,
      { manufacturerData: Omit<Manufacturer, 'id'>; logoFile?: File }
    >({
      queryFn: async ({ manufacturerData, logoFile }) => {
        const manufacturerService = getManufacturerService();
        const newManufacturer = await manufacturerService.createManufacturer(
          manufacturerData,
          logoFile
        );

        // Invalidate cache
        invalidateManufacturersCache();

        return { data: newManufacturer };
      },
      invalidatesTags: [{ type: 'Manufacturer', id: 'LIST' }],
    }),
    updateManufacturer: builder.mutation<
      Manufacturer,
      { id: string; manufacturerData: Partial<Manufacturer>; logoFile?: File }
    >({
      queryFn: async ({ id, manufacturerData, logoFile }) => {
        const manufacturerService = getManufacturerService();
        const updatedManufacturer = await manufacturerService.updateManufacturer(
          id,
          manufacturerData,
          logoFile
        );

        // Invalidate cache
        invalidateManufacturersCache();

        return { data: updatedManufacturer };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Manufacturer', id },
        { type: 'Manufacturer', id: 'LIST' },
      ],
    }),
    deleteManufacturer: builder.mutation<void, string>({
      queryFn: async id => {
        const manufacturerService = getManufacturerService();
        await manufacturerService.deleteManufacturer(id);

        // Invalidate cache
        invalidateManufacturersCache();

        return { data: undefined };
      },
      invalidatesTags: (_, __, id) => [
        { type: 'Manufacturer', id },
        { type: 'Manufacturer', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetManufacturersQuery,
  useGetAllManufacturersQuery,
  useGetActiveManufacturersQuery,
  useGetManufacturerByIdQuery,
  useAddManufacturerMutation,
  useUpdateManufacturerMutation,
  useDeleteManufacturerMutation,
  usePrefetch,
} = manufacturersApi;
